import { useState, useCallback, useMemo, useEffect } from "react";
import { SUG_MUTZAR_CATEGORY, SUG_MUTZAR_ICON } from "../utils/fieldMaps";
import { shortenCompanyName, normalizeId } from "../utils/formatters";
import { SOURCE_TYPE } from "../utils/dataSource";
import { memoryStore } from "../utils/storage";
import { useAuth } from "../contexts/AuthContext";

const STORAGE_KEY = "pension_clients_v1";
let _idSeq = 0;
const uniqueId = () => `${Date.now()}_${++_idSeq}`;

function migrateClient(c) {
  const oldClientId = c.fileInfo?.clientId || c.id || c.clientId;
  const normClientId = normalizeId(oldClientId);

  if (c.datasets) {
    c.clientId = normalizeId(c.clientId);
    if (c.fileInfo) c.fileInfo.clientId = normalizeId(c.fileInfo.clientId);
    return c;
  }
  return {
    ...c,
    clientId: normClientId,
    clientName: c.fileInfo?.client || "לקוח",
    activeDatasetIndex: 0,
    datasets: [{
      id: c.id,
      funds: c.funds,
      fileInfo: c.fileInfo ? { ...c.fileInfo, clientId: normClientId } : null,
      addedAt: c.addedAt,
      taarichBitzua: c.fileInfo?.taarichBitzua || null,
    }],
  };
}

function mergeByClientId(clients) {
  const map = new Map();
  for (const c of clients) {
    const key = c.clientId;
    if (!key || key === "—") {
      map.set(c.id, c);
      continue;
    }
    const existing = map.get(key);
    if (existing) {
      existing.datasets = [...existing.datasets, ...c.datasets];
      existing.activeDatasetIndex = existing.datasets.length - 1;
    } else {
      map.set(key, { ...c });
    }
  }
  // מזהים פנימיים כפולים: קובץ מרובה-לקוחות שנטען לפני שנוסף המונה
  // ל-uniqueId נתן לכמה לקוחות את אותו Date.now() כ-id — מה שגרם לאזהרת
  // key כפול ב-React ול-find לפי id להחזיר את הלקוח הלא נכון. הנגזרת
  // מהת"ז דטרמיניסטית, כך שכל המכשירים מתכנסים לאותו מזהה אחרי סנכרון.
  const seenIds = new Set();
  return Array.from(map.values()).map((c, i) => {
    let entry = c;
    if (seenIds.has(entry.id)) {
      entry = { ...entry, id: `${entry.id}_${entry.clientId || i}` };
    }
    seenIds.add(entry.id);
    return entry;
  });
}

export function useClientStore() {
  const { syncVersion } = useAuth();

  const [rawClients, setRawClients] = useState(() => {
    try {
      const raw = memoryStore.getItem(STORAGE_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      const migrated = mergeByClientId(arr.map(migrateClient));
      if (arr.length !== migrated.length) {
        memoryStore.setItem(STORAGE_KEY, JSON.stringify(migrated));
      }
      return migrated;
    } catch { return []; }
  });

  const clients = useMemo(() => {
    return rawClients.map(c => {
      const activeDs = c.datasets?.[c.activeDatasetIndex] || c.datasets?.[0];
      const funds = (activeDs?.funds || []).map(f => ({
        ...f,
        cat: SUG_MUTZAR_CATEGORY[f.sugMutzar] || f.cat,
        icon: SUG_MUTZAR_ICON[f.sugMutzar] || f.icon,
        companyName: shortenCompanyName(f.companyName),
        _sourceType: SOURCE_TYPE.MASLEKA, // טרום-ייעוץ תמיד מגיע מהמסלקה
      }));
      return {
        ...c,
        funds,
        fileInfo: activeDs?.fileInfo || c.fileInfo,
      };
    });
  }, [rawClients]);

  // Re-read from memoryStore when cloud sync completes (event-based)
  useEffect(() => {
    const onSync = () => {
      try {
        const raw = memoryStore.getItem(STORAGE_KEY);
        const arr = raw ? JSON.parse(raw) : [];
        setRawClients(mergeByClientId(arr.map(migrateClient)));
      } catch {}
    };
    window.addEventListener('pension-data-synced', onSync);
    return () => window.removeEventListener('pension-data-synced', onSync);
  }, []);

  // Re-read from memoryStore when AuthContext syncVersion increments (React state-based)
  useEffect(() => {
    if (syncVersion > 0) {
      try {
        const raw = memoryStore.getItem(STORAGE_KEY);
        const arr = raw ? JSON.parse(raw) : [];
        setRawClients(mergeByClientId(arr.map(migrateClient)));
      } catch {}
    }
  }, [syncVersion]);

  const [activeIds, setActiveIds] = useState([]);
  const [storageError, setStorageError] = useState(null);

  const persist = useCallback((newClients) => {
    try {
      memoryStore.setItem(STORAGE_KEY, JSON.stringify(newClients));
      setStorageError(null);
    } catch (e) {
      console.error("Failed to save to memoryStore", e);
    }
  }, []);

  const addClient = useCallback((funds, fileInfo, { navigate: doNav = true } = {}) => {
    const datasetId = uniqueId();
    const newDataset = {
      id: datasetId,
      funds,
      fileInfo,
      addedAt: new Date().toISOString(),
      taarichBitzua: fileInfo.taarichBitzua || null,
    };

    const normCid = normalizeId(fileInfo.clientId);
    // Compute activeId synchronously from current rawClients before the updater runs
    const existingClient = rawClients.find(
      c => c.clientId && normCid && normCid !== "—" && normalizeId(c.clientId) === normCid
    );
    const activeId = existingClient ? existingClient.id : datasetId;

    setRawClients(prev => {
      const existingIdx = prev.findIndex(
        c => c.clientId && normCid && normCid !== "—" && normalizeId(c.clientId) === normCid
      );

      let next;
      if (existingIdx >= 0) {
        next = [...prev];
        const existing = { ...next[existingIdx] };
        const dupIdx = existing.datasets.findIndex(ds => 
          ds.fileInfo?.name === fileInfo.name && 
          ds.fileInfo?.taarichBitzua === fileInfo.taarichBitzua
        );
        if (dupIdx >= 0) {
          const nextDatasets = [...existing.datasets];
          nextDatasets[dupIdx] = { ...newDataset, id: existing.datasets[dupIdx].id };
          existing.datasets = nextDatasets;
          existing.activeDatasetIndex = dupIdx;
        } else {
          existing.datasets = [...existing.datasets, newDataset];
          existing.activeDatasetIndex = existing.datasets.length - 1;
        }
        next[existingIdx] = existing;
      } else {
        const clientEntry = {
          id: datasetId,
          clientId: normCid,
          clientName: fileInfo.client,
          activeDatasetIndex: 0,
          datasets: [newDataset],
        };
        next = [...prev, clientEntry];
      }

      persist(next);
      return next;
    });

    if (doNav) setActiveIds([activeId]);
    return activeId;
  }, [persist, rawClients]);

  const setActiveDataset = useCallback((clientId, datasetIndex) => {
    setRawClients(prev => {
      const next = prev.map(c =>
        c.id === clientId
          ? { ...c, activeDatasetIndex: datasetIndex }
          : c
      );
      persist(next);
      return next;
    });
  }, [persist]);

  const removeClient = useCallback((id) => {
    setRawClients(prev => {
      const next = prev.filter(c => c.id !== id);
      persist(next);
      return next;
    });
    setActiveIds(prev => {
      const next = prev.filter(i => i !== id);
      return next.length ? next : [];
    });
  }, [persist]);

  const removeDataset = useCallback((clientId, datasetIndex) => {
    let removedClientId = null;
    setRawClients(prev => {
      const next = prev.map(c => {
        if (c.id !== clientId) return c;
        const newDs = c.datasets.filter((_, i) => i !== datasetIndex);
        if (!newDs.length) {
          removedClientId = c.id;
          return null; // מחק לקוח כולו אם אין datasets
        }
        // תקן את activeDatasetIndex — אם מחקנו לפניו, הורד ב-1
        const newActiveIdx = Math.max(
          0,
          Math.min(
            c.activeDatasetIndex >= datasetIndex && c.activeDatasetIndex > 0
              ? c.activeDatasetIndex - 1
              : c.activeDatasetIndex,
            newDs.length - 1
          )
        );
        return { ...c, datasets: newDs, activeDatasetIndex: newActiveIdx };
      }).filter(Boolean);
      persist(next);
      return next;
    });
    // אם הלקוח כולו נמחק — הוצא אותו מ-activeIds
    if (removedClientId) {
      setActiveIds(prev => {
        const next = prev.filter(i => i !== removedClientId);
        return next.length ? next : [];
      });
    }
  }, [persist]);

  const toggleCompare = useCallback((id) => {
    setActiveIds(prev => {
      if (prev.includes(id)) {
        if (prev.length === 1) return prev;
        const next = prev.filter(i => i !== id);
        return next;
      }
      if (prev.length >= 2) {
        const emptyIdx = prev.indexOf(null);
        if (emptyIdx !== -1) {
          const next = [...prev];
          next[emptyIdx] = id;
          return next;
        }
        return [prev[0], id];
      }
      return [...prev, id];
    });
  }, []);

  const setSlot = useCallback((index, id) => {
    setActiveIds(prev => {
      const next = [...prev];
      while (next.length <= index) next.push(null);
      next[index] = id;
      return next;
    });
  }, []);

  const closeSlot = useCallback((index) => {
    setActiveIds(prev => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((_, i) => i !== index);
      return next;
    });
  }, []);

  const openSplit = useCallback(() => {
    setActiveIds(prev => {
      if (prev.length >= 2) return prev;
      return [...prev, null];
    });
  }, []);

  const removeDatasetByFileName = useCallback((fileName) => {
    let removedClientIds = [];
    setRawClients(prev => {
      let changed = false;
      const next = prev.map(c => {
        const newDs = c.datasets.filter(ds => ds.fileInfo?.fileName !== fileName);
        if (newDs.length === c.datasets.length) return c;
        changed = true;
        if (!newDs.length) {
          removedClientIds.push(c.id);
          return null;
        }
        const newActiveIdx = Math.min(c.activeDatasetIndex, Math.max(0, newDs.length - 1));
        return { ...c, datasets: newDs, activeDatasetIndex: newActiveIdx };
      }).filter(Boolean);
      if (changed) persist(next);
      return next;
    });
    
    if (removedClientIds.length > 0) {
      setActiveIds(prev => {
        const next = prev.filter(i => !removedClientIds.includes(i));
        return next.length ? next : [];
      });
    }
  }, [persist]);

  return {
    clients, activeIds, setActiveIds,
    addClient, removeClient, removeDataset, removeDatasetByFileName, toggleCompare,
    setSlot, closeSlot, openSplit,
    setActiveDataset,
    storageError
  };
}
